import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth, isSuperAdmin } from "../contexts/AuthContext";
import Icon from "./ui/Icon";
import ChatWidget from "./ChatWidget";
import { useTheme } from "../theme";
import "./ui/ui.css";
import "./PanelLayout.css";

// Sidebar entries. Add a line here and a <Route> in App.js for each new page.
// `superAdmin` entries are hidden from admins (their routes are guarded too).
const NAV_ITEMS = [
  { to: "/inventory", label: "Ad inventory", icon: "table" },
  { to: "/plans", label: "Media plans", icon: "briefcase" },
  { to: "/recent", label: "Recent changes", icon: "clock", superAdmin: true },
  { to: "/users", label: "Users", icon: "users", superAdmin: true },
];

const EXPANDED_KEY = "inv_panel_sidebar_expanded";
const MOBILE = "(max-width: 768px)";

// Desktop starts with the narrow icon rail; opening it is remembered
function loadExpanded() {
  try {
    return localStorage.getItem(EXPANDED_KEY) === "1";
  } catch {
    return false;
  }
}

export default function PanelLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  // Desktop: rail (collapsed) or full width. Phones: hidden or slid over.
  const [expanded, setExpanded] = useState(loadExpanded);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { theme, toggle: toggleTheme } = useTheme();

  useEffect(() => {
    try {
      localStorage.setItem(EXPANDED_KEY, expanded ? "1" : "0");
    } catch {
      // not remembered, still works
    }
  }, [expanded]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e) => e.key === "Escape" && setMobileOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  const isMobile = () => window.matchMedia(MOBILE).matches;
  const menuOpen = isMobile() ? mobileOpen : expanded;

  const toggleMenu = () => {
    if (isMobile()) setMobileOpen((o) => !o);
    else setExpanded((o) => !o);
  };

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="panel">
      <header className="panel-topbar">
        <button
          className="panel-menu-btn"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          onClick={toggleMenu}
        >
          <span />
          <span />
          <span />
        </button>

        <NavLink to="/inventory" className="panel-logo">
          <img
            src={theme === "dark" ? "/admongrel-wordmark-dark.webp" : "/admongrel-wordmark.webp"}
            alt="admongrel"
          />
          <span className="panel-logo-tag">Adventory</span>
        </NavLink>

        <div className="panel-user">
          {user?.email && <span className="panel-user-email">{user.email}</span>}
          <button
            type="button"
            className="icon-btn panel-theme-btn"
            onClick={toggleTheme}
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          >
            <Icon name={theme === "dark" ? "sun" : "moon"} size={18} />
          </button>
          <button className="panel-logout" onClick={handleLogout}>
            Log out
          </button>
        </div>
      </header>

      <div className="panel-body">
        <aside
          className={`panel-sidebar${expanded ? " is-expanded" : ""}${mobileOpen ? " is-open" : ""}`}
        >
          <nav>
            {NAV_ITEMS.filter((item) => !item.superAdmin || isSuperAdmin(user)).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className="panel-nav-link"
                title={item.label}
                onClick={() => setMobileOpen(false)}
              >
                <Icon name={item.icon} size={18} />
                <span className="panel-nav-label">{item.label}</span>
              </NavLink>
            ))}
          </nav>
        </aside>

        {mobileOpen && (
          <div className="panel-backdrop" onClick={() => setMobileOpen(false)} aria-hidden="true" />
        )}

        <main className="panel-main">
          <Outlet />
        </main>
      </div>

      <ChatWidget />
    </div>
  );
}
